/**
 * "Nutzung"-Tab im Mandanten-Kontrollzentrum (Admin-Bereich V2 P1, docs/KONZEPT-ADMIN-BEREICH.md
 * § "Mandanten-Detail, 8 Tabs" Punkt 5, dort "Nutzung & Kosten": "KI-Kosten, Google-Places-
 * Aufrufe, Mails+Bounce-Quote ..., Geschäfts-KPIs aus `business_metric_periods`").
 *
 * PRAEZISIERUNG gegenueber dem Konzept-Wortlaut: "monatsscharf" beschreibt Migration 378
 * (`tenant_settings`/Betrieb, P4) — die tatsaechlich existierende Route
 * `GET /api/admin/tenants/:id/nutzung` (Migration 373) liefert eine ROLLIERENDE Tages-Zeitreihe
 * (Default 30 Tage, gedeckelt auf 90, siehe `packages/db/sql/373_platform_admin_nutzung.sql`
 * Kopfkommentar), keine Kalendermonate. Dieser Tab beschriftet deshalb ehrlich "(30 Tage)" statt
 * einen Kalendermonat vorzutaeuschen, den die Daten nicht hergeben. Google-Places-Aufrufe
 * (`google_places_verbrauch`) und Geschaefts-KPIs (`business_metric_periods`) hat KEINE
 * Admin-Route — beides bewusst NICHT erfunden, sondern als offener Punkt benannt (Tab-Fussnote
 * unten + Bericht der Iteration).
 */
import { useApi } from "../../../hooks/useApi";
import { EmptyState, ErrorNote, KpiCard, LoadingRows } from "../../../ui/components";
import { money } from "../../../ui/format";

/** 1:1 aus `apps/api/src/routes/admin.ts` `NutzungZeitreiheTag` uebernommen. `tag` kommt als
 *  reiner "YYYY-MM-DD"-String an (siehe `zuDatumString` dort) — bewusst NICHT ueber `new Date(...)`
 *  geparst (Zeitzonen-Fallstrick bei reinen Datumsstrings), sondern textuell umsortiert. */
interface NutzungZeitreiheTag {
  tag: string;
  fehler: number;
  mails: number;
  bounces: number;
  bounceQuote: number | null;
  kiKostenCents: number;
}

function formatTag(tag: string): string {
  const [jahr, monat, tagZahl] = tag.split("-");
  if (!jahr || !monat || !tagZahl) return tag;
  return `${tagZahl}.${monat}.${jahr}`;
}

/** `bounced / mails` als deutsch formatierte Prozentzahl, `null` bei fehlendem Nenner (siehe
 *  `bounceQuote()`-Kommentar in `apps/api/src/routes/admin.ts`: eine Quote ohne Mails waere
 *  irrefuehrend, nicht "0 %"). */
function formatQuote(value: number | null): string {
  if (value == null) return "–";
  return `${(value * 100).toFixed(1).replace(".", ",")} %`;
}

export function NutzungTab({ tenantId }: { tenantId: string }) {
  const { data, zusatz, loading, error, reload } = useApi<NutzungZeitreiheTag[], { bounceQuoteHinweis?: string }>(
    `/api/admin/tenants/${tenantId}/nutzung`,
  );

  if (loading) {
    return (
      <div className="card mt-4 overflow-hidden">
        <LoadingRows rows={4} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="card mt-4 overflow-hidden">
        <ErrorNote message={error} onRetry={reload} />
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="card mt-4 overflow-hidden">
        <EmptyState icon="payments" title="Keine Nutzungsdaten" hint="Für diesen Mandanten liegt noch keine Zeitreihe vor." />
      </div>
    );
  }

  const totalMails = data.reduce((summe, tag) => summe + tag.mails, 0);
  const totalBounces = data.reduce((summe, tag) => summe + tag.bounces, 0);
  const totalKiCents = data.reduce((summe, tag) => summe + tag.kiKostenCents, 0);
  const gesamtQuote = totalMails > 0 ? totalBounces / totalMails : null;

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label={`KI-Kosten (${data.length} Tage)`} value={money(totalKiCents)} icon="smart_toy" />
        <KpiCard label={`Mails versendet (${data.length} Tage)`} value={totalMails} icon="mail" />
        <KpiCard
          label={`Bounce-Quote (${data.length} Tage)`}
          value={formatQuote(gesamtQuote)}
          icon="report"
          iconTone={gesamtQuote != null && gesamtQuote > 0 ? "warn" : "success"}
        />
      </div>
      {zusatz?.bounceQuoteHinweis && (
        <p className="font-body-sm text-[12px] text-secondary-fixed-dim">{zusatz.bounceQuoteHinweis}</p>
      )}
      <p className="font-body-sm text-[12.5px] text-secondary-fixed-dim">
        Google-Places-Aufrufe und Geschäfts-KPIs (<code>business_metric_periods</code>) sind über die
        Admin-API aktuell nicht verfügbar — offener Punkt für eine spätere Iteration.
      </p>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="handy-karten w-full min-w-[560px] border-collapse text-left">
            <thead>
              <tr>
                <th className="table-header">Tag</th>
                <th className="table-header">Fehler</th>
                <th className="table-header">Mails</th>
                <th className="table-header">Bounces</th>
                <th className="table-header">KI-Kosten</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
              {data.map((tag) => (
                <tr key={tag.tag}>
                  <td className="table-cell whitespace-nowrap font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    {formatTag(tag.tag)}
                  </td>
                  <td className="table-cell font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    {tag.fehler}
                  </td>
                  <td className="table-cell font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    {tag.mails}
                  </td>
                  <td className="table-cell font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    {tag.bounces}
                  </td>
                  <td className="table-cell font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    {money(tag.kiKostenCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
